/**
 * Helper utility to sort reports by `created_at` in descending order (newest first).
 * If timestamps are missing or equal, uses a stable tie-breaker on report `id`.
 */
export const sortReportsByCreatedAtDesc = <T extends { created_at?: string | Date | null; id?: string | number }>(
  reports: T[]
): T[] => {
  if (!Array.isArray(reports)) return [];

  return [...reports].sort((a, b) => {
    const timeA = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const timeB = b?.created_at ? new Date(b.created_at).getTime() : 0;

    const validA = !isNaN(timeA) && timeA > 0;
    const validB = !isNaN(timeB) && timeB > 0;

    if (!validA && !validB) return 0;
    if (!validA) return 1; // Invalid or missing date sorted last
    if (!validB) return -1;

    if (timeB !== timeA) {
      return timeB - timeA; // Descending (newest first)
    }

    // Stable tie-breaker when created_at timestamps are equal
    const idA = String(a?.id ?? '');
    const idB = String(b?.id ?? '');
    return idB.localeCompare(idA);
  });
};
